<!-- eslint-disable no-prototype-builtins -->
<script setup lang="ts">
import AppState, {
  contactLabelPluralLower,
  contactLabelSingularLower,
  getFormsInLocation,
  getNestedCustomValues,
  setEditCalendarValidationData,
} from '@/states/app'
import {
  DotsVerticalIcon,
  InfoCircleIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UITooltip as GHLUITooltip,
  UICheckbox,
  UIDivider,
  UIFormItem,
  UIInput,
  UIRadio,
  UISelect,
  UISwitch,
} from '@gohighlevel/ghl-ui'
import { computed, reactive, ref, watch } from 'vue'
import { VueDraggableNext } from 'vue-draggable-next'
import { useI18n } from 'vue-i18n'
import {
  GuestCollectionMode,
  GuestType,
  WidgetConfig,
  WidgetType,
} from '../../models/calendar'
import {
  handleNestedCustomValueModification,
  thankyouMessageFieldOptions,
  thankyouMessageFieldOptionsV3,
  thankyouMessageFieldRecurringOptions,
} from '../../utils/customValueMapper'
import { getTooltip } from '../../utils/generalFunctions'
import DependentSettings from '../common/DependentSettings.vue'
import HeaderTile from '../common/HeaderTile.vue'
import Tooltip from '../common/Tooltip.vue'
import CustomVariablePickerV2 from '../dynamic/CustomVariablePickerV2.vue'
import AddGuestModal from './AddGuestModal.vue'

const defaultPageOrder = [
  {
    position: 0,
    kind: 'calendar',
  },
  {
    position: 1,
    kind: 'form',
  },
]

const { t } = useI18n()

const state = reactive({
  isLoading: false,
  shouldAcceptPayment: false,
  reorderedPageOption: [] as { position: number; kind: string }[] | undefined,
  shouldAcceptDepositAmount: false,
  isClassicWidget: false,
  formData: {
    stickyContact: false,
    autoConfirm: true,
    formId: '',
    pixelId: '',
    formSubmitType: 'ThankYouMessage',
    formSubmitRedirectURL: '',
    formSubmitThanksMessage: '',
    isLivePaymentMode: false,
    enableGuests: false,
    enableMandatoryGuestType: false,
    guestType: GuestType.COLLECT_DETAIL,
    guestCollectionMode: GuestCollectionMode.EMAIL,
    shouldSendCustomForm: false,
    enableConsentCheck: false,
    consentLabel: '' as string | undefined,
    widgetConfig: undefined as WidgetConfig | undefined,
  },
  isFormFirstEnabled: false,
  isPaymentError: false,
  showDependentSettings: false
})
const listLabels = {
  calendar: t('calendar_advanced.forms_payment.date_and_time_selector'),
  form: t('calendar_advanced.forms_payment.form_selector'),
}

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const isClassBookingCalendar = computed(() => {
  return editingCalendar.value?.isClassBookingCalendar
})

const isServiceCalendar = computed(() => {
  return editingCalendar.value?.isServiceCalendar
})

const enableAddGuests = computed(() => {
  return !isClassBookingCalendar.value && !isServiceCalendar.value
})

const disableFormOrder = computed(() => {
  return AppState.isClassicWidgetSelected
})

const guestCollectionModeOptions = computed(() => [
  {
    value: GuestCollectionMode.EMAIL,
    label: t('calendar_advanced.forms_payment.guest_collection_mode_email'),
  },
  {
    value: GuestCollectionMode.PHONE,
    label: t('calendar_advanced.forms_payment.guest_collection_mode_phone'),
  },
  {
    value: GuestCollectionMode.EMAIL_AND_PHONE,
    label: t(
      'calendar_advanced.forms_payment.guest_collection_mode_email_phone'
    ),
  },
])

const formOptions = computed(() => {
  const customFormOptions = AppState.forms.map(x => {
    return {
      value: x._id,
      label: x.name,
    }
  })

  return [
    {
      value: '',
      label: t('calendar_advanced.forms_payment.default_form'),
    },
    ...customFormOptions,
  ]
})

watch(
  () => formOptions.value,
  () => {
    if (formOptions.value.length === 0) {
      return
    }

    const formOption = formOptions.value.find(
      x => x.value === state.formData.formId
    )

    if (!formOption) {
      state.formData.formId = ''
    }
  }
)

watch(
  () => state.formData.enableGuests,
  () => {
    setEditCalendarValidationData('enableGuests', state.formData.enableGuests)
  },
  { deep: true }
)

watch(
  () => state.formData.enableMandatoryGuestType,
  () => {
    setEditCalendarValidationData(
      'enableMandatoryGuestType',
      state.formData.enableMandatoryGuestType
    )
  },
  { deep: true }
)

watch(
  () => state.isFormFirstEnabled,
  () => {
    AppState.isFormFirstEnabled = state.isFormFirstEnabled
  }
)

watch(
  () => AppState.editCalendarValidationData.disableFormFirst,
  newValue => {
    if (newValue) {
      state.isFormFirstEnabled = false
      AppState.isFormFirstEnabled = false
      state.formData.widgetConfig.pageOrder = [
        { position: 0, kind: 'calendar' },
        { position: 1, kind: 'form' },
      ]
      state.reorderedPageOption = state.formData.widgetConfig?.pageOrder
    }
  }
)

watch(
  () => AppState.isFormFirstEnabled,
  newValue => {
    if (!newValue) {
      return
    }

    const { widgetConfig } = state.formData || {}
    if (widgetConfig?.pageOrder) {
      widgetConfig.pageOrder.sort((a, b) => (a.kind === 'form' ? -1 : 1))
    } else {
      state.formData.widgetConfig.pageOrder = [
        { position: 1, kind: 'calendar' },
        { position: 0, kind: 'form' },
      ]
    }
  }
)

watch(
  () => state.formData.formId,
  () => {
    if (!state.formData.formId && state.formData.formSubmitType === 'CustomForm') {
      state.formData.formSubmitType = 'ThankYouMessage'
    }
  }
)


const emit = defineEmits(['onSave', 'onCancel'])

const thankyouMessageRef = ref(thankyouMessageFieldOptionsV3)
const thankyouMessageRecurringRef = ref(thankyouMessageFieldRecurringOptions)

// Function to load and update custom values with nested folder structure
const updateThankyouMessageRefs = async () => {
  const thankyouOptions = editingCalendar.value?.isEventCalendar
    ? thankyouMessageFieldOptions
    : thankyouMessageFieldOptionsV3

  // Set base options first (synchronous)
  thankyouMessageRef.value = thankyouOptions
  thankyouMessageRecurringRef.value = thankyouMessageFieldRecurringOptions

  // Then fetch and add custom values (asynchronous)
  const nestedCustomValues = await getNestedCustomValues()
  const customValues = handleNestedCustomValueModification(nestedCustomValues)

  if (customValues) {
    thankyouMessageRef.value = [...thankyouOptions, customValues]
    thankyouMessageRecurringRef.value = [
      ...thankyouMessageFieldRecurringOptions,
      customValues,
    ]
  }
}

watch(
  () => editingCalendar.value?.id,
  newId => {
    if (newId) {
      updateThankyouMessageRefs()
    }
  },
  { immediate: true }
)

const methods = {
  async loadData() {
    state.shouldAcceptPayment = Boolean(
      editingCalendar.value?.stripe?.hasOwnProperty('amount')
    )
    state.shouldAcceptDepositAmount = Boolean(
      editingCalendar.value?.stripe?.hasOwnProperty('deposit')
    )
    state.formData = {
      stickyContact: Boolean(editingCalendar.value?.stickyContact),
      isLivePaymentMode: Boolean(editingCalendar.value?.isLivePaymentMode),
      autoConfirm: Boolean(editingCalendar.value?.autoConfirm),
      formId: editingCalendar.value?.formId || '',
      pixelId: editingCalendar.value?.pixelId || '',
      formSubmitType:
        editingCalendar.value?.formSubmitType || 'ThankYouMessage',
      formSubmitRedirectURL: editingCalendar.value?.formSubmitRedirectURL || '',
      formSubmitThanksMessage:
        editingCalendar.value?.formSubmitThanksMessage || '',
      enableGuests: Boolean(editingCalendar.value?.enableGuests),
      enableMandatoryGuestType: Boolean(
        editingCalendar.value?.enableMandatoryGuestType
      ),
      guestType: editingCalendar.value?.guestType || GuestType.COLLECT_DETAIL,
      guestCollectionMode:
        editingCalendar.value?.guestCollectionMode || GuestCollectionMode.EMAIL,
      shouldSendCustomForm: Boolean(
        editingCalendar.value?.shouldSendCustomForm
      ),
      enableConsentCheck: Boolean(editingCalendar.value?.enableConsentCheck),
      consentLabel: editingCalendar.value?.consentLabel,
      widgetConfig: editingCalendar.value?.widgetConfig || {},
    }
    if (state.formData.widgetConfig) {
      state.formData.widgetConfig.pageOrder =
        state.formData?.widgetConfig?.pageOrder || defaultPageOrder
    }
    state.isClassicWidget =
      WidgetType.CLASSIC === editingCalendar.value?.widgetType
    state.reorderedPageOption = state.formData.widgetConfig?.pageOrder
    const pageOrder = state.reorderedPageOption?.find(
      (x: any) => x.kind === 'form'
    )
    state.isFormFirstEnabled = pageOrder?.position === 0
    await Promise.all([getFormsInLocation()])
  },
  async emitSave() {
    const payload = {
      ...state.formData,
    } as any

    const reorderedPageOption = state.reorderedPageOption?.map(
      (item, index) => ({
        kind: item?.kind,
        position: index,
      })
    )
    if (reorderedPageOption?.length) {
      payload.widgetConfig.pageOrder = reorderedPageOption
    }

    return payload
  },
  handleOptionChange(e) {
    const pageOrder = state.reorderedPageOption?.at(0)
    state.isFormFirstEnabled = pageOrder?.kind === 'form'
    AppState.isFormFirstEnabled = pageOrder?.kind === 'form'
    if (
      !state.isFormFirstEnabled &&
      AppState.editCalendarValidationData.enableSameUserAssignment
    ) {
      state.showDependentSettings = true
    }
  },
}

defineExpose({
  methods,
})

watch(
  () => editingCalendar.value,
  () => {
    if (editingCalendar.value) {
      methods.loadData()
    }
  },
  { immediate: true }
)

watch(
  () => state.shouldAcceptDepositAmount,
  () => (AppState.isPartialPaymentChecked = state.shouldAcceptDepositAmount)
)

// Define a ref to control the modal visibility
const showModal = ref(false)

// Method to handle the UISwitch toggle
const handleEnableGuestToggle = value => {
  state.formData.enableGuests = value
  if (value && !editingCalendar.value?.hasStoredGuestCollectionMode) {
    // Enabling guest details on a calendar that never stored a collection
    // mode (new calendars in the create-then-edit flow) defaults to the
    // richest capture per PRD; calendars with a saved mode keep it.
    state.formData.guestCollectionMode = GuestCollectionMode.EMAIL_AND_PHONE
  }
  if (value && AppState.editCalendarValidationData.shouldAcceptPayment) {
    showModal.value = true
  }
  if (!value) {
    AppState.editCalendarValidationData.enableChargeGuests = false
    state.formData.enableMandatoryGuestType = false
  }
}

const handleGuestModalConfirm = (enableChargeGuests: boolean) => {
  AppState.editCalendarValidationData.enableChargeGuests = enableChargeGuests
}

watch(
  () => AppState.isPartialPaymentChecked,
  () => {
    if (!AppState.isPartialPaymentChecked) {
      state.shouldAcceptDepositAmount = false
    }
  }
)

const insertThanksMessage = (value: string) => {
  state.formData.formSubmitThanksMessage =
    state.formData.formSubmitThanksMessage + ' ' + value
}

const cancelDependentSettings = () => {
  state.showDependentSettings = false
  state.formData.widgetConfig.pageOrder = [
    { position: 0, kind: 'form' },
    { position: 1, kind: 'calendar' },
  ]
  state.reorderedPageOption = state.formData.widgetConfig?.pageOrder
}

const confirmDependentSettings = () => {
  state.showDependentSettings = false
  state.isFormFirstEnabled = false
  AppState.editCalendarValidationData.enableSameUserAssignment = false
}
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="calendars-forms-payments-tab"
      :header="$t('calendar_advanced.forms_payment.forms')"
      :description="t('calendar_advanced.forms_payment.forms_description')"
    />
    <div class="my-[15px] border-t border-gray-200" />

    <UIFormItem
      :label="$t('calendar_advanced.forms_payment.select_form')"
      path="formData.formId"
      :tooltip="
        getTooltip($t('calendar_advanced.forms_payment.select_form_tooltip'))
      "
    >
      <div class="mt-1 flex flex-col">
        <UISelect
          id="calendar-selectForm"
          v-model:value="state.formData.formId"
          class="w-[430px]"
          :filterable="true"
          :options="formOptions"
        />
      </div>
    </UIFormItem>
    <UIFormItem
      :label="t('calendar_advanced.forms_payment.widget_order_label')"
      path="formData.formId"
      :tooltip="
        getTooltip(t('calendar_advanced.forms_payment.widget_order_tool_tip'))
      "
    >
      <div
        v-if="state.formData.widgetConfig?.pageOrder"
        class="flex w-full flex-row"
      >
        <div class="w-[8%] px-2">
          <div
            v-for="(_, index) in state.formData.widgetConfig?.pageOrder"
            :key="'step-' + index"
            class="flex w-full cursor-move items-center space-x-5 py-3"
          >
            <div class="w-full font-medium text-gray-700">
              {{
                `${t('calendar_advanced.forms_payment.order_step')} ${
                  index + 1
                } `
              }}
            </div>
          </div>
        </div>
        <VueDraggableNext
        :list="state.reorderedPageOption"
        :fallback-on-body="true"
        :force-fallback="true"
        class="dragArea list-group w-[36%] rounded-md border"
        :disabled="disableFormOrder"
        @change="methods.handleOptionChange"
      >
        <transition-group name=" flip-list">
          <div
            v-for="(pageOrder, index) in state.formData.widgetConfig?.pageOrder"
            :key="pageOrder?.kind + index"
            :class="disableFormOrder ? 'cursor-no-drop' : 'cursor-move'"
          >
            <GHLUITooltip
              :trigger="disableFormOrder ? 'hover' : 'manual'"
              placement="top"
            >
              <template #trigger>
              <div
                class="flex cursor-move items-center space-x-5 border py-4 font-medium"
                :class="disableFormOrder ? 'cursor-no-drop' : 'cursor-move'"
              >
                <div class="pl-2">
                  <DotsVerticalIcon class="h-5 w-4 text-gray-500" />
                  <DotsVerticalIcon class="-ml-2 h-5 w-4 text-gray-500" />
                </div>
                <div class="capitalize text-gray-700">
                  {{ listLabels[pageOrder.kind] }}
                </div>
              </div>
              </template>
              <div class="w-48 text-white">
                {{ $t('calendar_advanced.forms_payment.widget_re_order_tooltip') }}
              </div>
            </GHLUITooltip>
          </div>
        </transition-group>
      </VueDraggableNext>
      </div>
    </UIFormItem>

    <!-- Sticky Contacts (Only if Default Form is selected) -->
    <div
      v-if="state.formData.formId === ''"
      class="flex flex-col items-start gap-2 border-b border-t border-gray-200 py-[15px]"
    >
      <div class="mb-[3px] font-medium text-gray-700">
        {{
          $t('calendar_advanced.forms_payment.sticky_contacts_label', {
            contactsLabel: contactLabelPluralLower,
          })
        }}
      </div>
      <div class="flex flex-row gap-2">
        <div id="enable-sticky-contacts">
          <UISwitch
            v-model:value="state.formData.stickyContact"
            size="small"
            :aria-label="t('calendar_advanced.forms_payment.sticky_contacts')"
          />
        </div>
        <div class="mr-[5px] font-normal text-gray-700">
          {{ $t('calendar_advanced.forms_payment.sticky_contacts') }}
        </div>
        <Tooltip
          :message="
            t('calendar_advanced.forms_payment.sticky_contacts_tooltip', {
              contactsLabel: contactLabelPluralLower,
            })
          "
        />
      </div>
    </div>

    <!-- Consent Form (Only if Default Form is selected) -->
    <div
      v-if="state.formData.formId === ''"
      class="flex flex-col items-start gap-2 py-[15px]"
    >
      <div class="mb-[3px] font-medium text-gray-700">
        {{ $t('calendar_advanced.forms_payment.consent_label') }}
      </div>
      <div class="flex flex-row gap-2">
        <div id="consent-form-toggle">
          <UISwitch
            v-model:value="state.formData.enableConsentCheck"
            size="small"
            :aria-label="t('calendar_advanced.forms_payment.consent_label')"
          />
        </div>
        <div class="mr-[5px] font-normal text-gray-700">
          {{ $t('calendar_advanced.forms_payment.consent_label') }}
        </div>
        <Tooltip
          :message="
            $t('calendar_advanced.forms_payment.consent_tooltip')
          "
        />
      </div>
      <UIInput
        v-if="state.formData.enableConsentCheck"
        id="consent-form-input"
        v-model:value="state.formData.consentLabel"
        type="textarea"
        :rows="4"
        class="mb-2 !w-full items-center"
        :placeholder="
          $t('calendar_advanced.forms_payment.consent_input_placeholder')
        "
      />
    </div>

    <!-- Guests  -->
    <div
      v-if="enableAddGuests"
      class="flex flex-col items-start gap-2 border-t border-gray-200 py-[15px]"
    >
      <div class="mb-[3px] font-medium text-gray-700">
        {{ $t('calendar_advanced.forms_payment.guests') }}
      </div>
      <div class="flex flex-row gap-2">
        <div id="enable-guest-toggle">
          <UISwitch
            v-model:value="state.formData.enableGuests"
            size="small"
            :aria-label="t('aria_labels.enable_guests')"
            @update:value="value => handleEnableGuestToggle(value)"
          />
        </div>
        <div class="mr-[5px] font-normal text-gray-700">
          {{ $t('calendar_advanced.forms_payment.enable_guests') }}
        </div>
        <Tooltip
          :message="$t('calendar_advanced.forms_payment.add_guest_tooltip')"
        />
      </div>
      <div v-if="state.formData.enableGuests" class="mt-2 flex">
        <UIRadio
          id="calendar-guest-type-collect-detail"
          class="p-1"
          :checked="state.formData.guestType === GuestType.COLLECT_DETAIL"
          name="custom"
          @change="state.formData.guestType = GuestType.COLLECT_DETAIL"
        >
          {{ $t('calendar_advanced.forms_payment.add_guest_collect_detail') }}
        </UIRadio>
        <UIRadio
          id="calendar-guest-type-count-only"
          class="p-1"
          :checked="state.formData.guestType === GuestType.COUNT_ONLY"
          name="default"
          @change="state.formData.guestType = GuestType.COUNT_ONLY"
        >
          {{ $t('calendar_advanced.forms_payment.add_guest_count_only') }}
        </UIRadio>
      </div>
      <div
        v-if="
          state.formData.enableGuests &&
          state.formData.guestType === GuestType.COLLECT_DETAIL
        "
        class="mt-2"
      >
        <UIFormItem
          :label="
            $t('calendar_advanced.forms_payment.guest_collection_mode_label')
          "
        >
          <UISelect
            id="calendar-guest-collection-mode"
            v-model:value="state.formData.guestCollectionMode"
            class="w-[430px]"
            :options="guestCollectionModeOptions"
          />
        </UIFormItem>
      </div>
      <div
        v-if="
          state.formData.enableGuests &&
          state.formData.formId !== '' &&
          state.formData.guestType === GuestType.COLLECT_DETAIL
        "
        class="ml-1 mt-4 flex flex-row items-center"
      >
        <UICheckbox
          id="calendar-guest-custom-form-notification"
          v-model:checked="state.formData.shouldSendCustomForm"
        >
          {{ $t('calendar_advanced.forms_payment.custom_email_notification') }}
        </UICheckbox>
        <Tooltip
          :message="
            $t('calendar_advanced.forms_payment.add_guest_custom_form_tooltip')
          "
        />
      </div>

      <div v-if="state.formData.enableGuests" class="mt-4">
        <UICheckbox
          id="calendar-guest-mandatory-checkbox"
          v-model:checked="state.formData.enableMandatoryGuestType"
          class="ml-1 flex flex-row items-start"
        >
          <div class="mt-[-1px] text-[14px] font-normal text-gray-900">
            {{
              $t('calendar_advanced.forms_payment.require_guest_for_booking')
            }}
            <div class="mb-2 text-[12px] leading-6 text-gray-500">
              {{ $t('calendar_advanced.forms_payment.enable_mandatory_guest') }}
            </div>
          </div>
        </UICheckbox>
      </div>
    </div>
  </div>

  <div class="mt-[16px] rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      id="calendars-forms-payments-tab"
      :header="$t('calendar_advanced.forms_payment.confirmation_page')"
      :description="t('calendar_advanced.forms_payment.forms_sub')"
    />
    <div class="mt-[16px] flex flex-col border-t border-gray-200 py-[16px]">
      <div class="mb-[16px] flex flex-wrap items-center gap-x-4 gap-y-2">
        <UITooltip
          :title="$t('calendar_advanced.forms_payment.default_message_tooltip')"
          placement="top"
        >
          <div class="inline-flex items-center">
            <UIRadio
              id="calendar-default-thankyou-message"
              :checked="state.formData.formSubmitType === 'ThankYouMessage'"
              name="formSubmitType"
              @change="state.formData.formSubmitType = 'ThankYouMessage'"
            >
              {{ $t("calendar_advanced.forms_payment.default") }}
            </UIRadio>
          </div>
        </UITooltip>
        <UITooltip
          :title="$t('calendar_advanced.forms_payment.redirect_url_tooltip')"
          placement="top"
        >
          <div class="inline-flex items-center">
            <UIRadio
              id="calendar-redirect-url"
              :checked="state.formData.formSubmitType === 'RedirectURL'"
              name="formSubmitType"
              @change="state.formData.formSubmitType = 'RedirectURL'"
            >
              {{ $t("calendar_advanced.forms_payment.redirect_url") }}
            </UIRadio>
          </div>
        </UITooltip>
        <UITooltip
          :title="
            $t('calendar_advanced.forms_payment.use_custom_form_rules_tooltip')
          "
          placement="top"
          v-if="Boolean(state.formData.formId) "
        >
          <div class="inline-flex items-center">
            <UIRadio
              id="calendar-custom-form"
              :checked="state.formData.formSubmitType === 'CustomForm'"
              name="formSubmitType"
              @change="state.formData.formSubmitType = 'CustomForm'"
            >
              {{ $t("calendar_advanced.forms_payment.use_custom_form_rules") }}
            </UIRadio>
          </div>
        </UITooltip>
      </div>

      <div
        v-if="state.formData.formSubmitType === 'ThankYouMessage'"
        class="w-full"
      >
        <UIFormItem
          :label="$t('calendar_advanced.forms_payment.thank_you')"
          path="formData.formSubmitThanksMessage"
          :show-feedback="false"
        >
          <UIInput
            id="redirect-url-input"
            v-model:value="state.formData.formSubmitThanksMessage"
            type="textarea"
            :rows="4"
            class="relative"
            :placeholder="
              $t('calendar_advanced.forms_payment.enter_thank_you_message')
            "
          >
            <template #suffix>
              <CustomVariablePickerV2
                :dropdown-options="
                  AppState.editCalendarValidationData.enableRecurring
                    ? thankyouMessageRecurringRef
                    : thankyouMessageRef
                "
                :button-class="'absolute bottom-0 right-4 cursor-pointer'"
                @update:selected-value="(val) => insertThanksMessage(val)"
              />
            </template>
          </UIInput>
        </UIFormItem>
      </div>

      <div
        v-if="state.formData.formSubmitType === 'RedirectURL'"
        class="w-full"
      >
        <UIInput
          id="redirect-url-input"
          v-model:value="state.formData.formSubmitRedirectURL"
          :placeholder="$t('calendar_advanced.forms_payment.enter_url')"
        />
      </div>
    </div>

    <UIFormItem
      :label="$t('calendar_advanced.forms_payment.pixel_id')"
      path="formData.pixelId"
      :tooltip="
        getTooltip($t('calendar_advanced.forms_payment.pixel_id_tooltip'))
      "
      :show-feedback="false"
      class="mt-[20px]"
    >
      <div class="w-[430px]">
        <UIInput
          id="calendar-pixel-id"
          v-model="state.formData.pixelId"
          :placeholder="t('placeholders.pixel_id')"
        />
      </div>
    </UIFormItem>

    <UIDivider />

    <div class="flex items-center space-x-3 pb-3">
      <div id="calendar-autoConfirm-switch">
        <UISwitch
          v-model:value="state.formData.autoConfirm"
          :aria-label="t('aria_labels.auto_confirm')"
          size="small"
        />
      </div>
      <div class="font-normal">
        {{
          isServiceCalendar
            ? $t("calendar_advanced.forms_payment.auto_confirm_service")
            : $t("calendar_advanced.forms_payment.auto_confirm")
        }}
      </div>
      <UITooltip
        :title="$t('calendar_advanced.forms_payment.auto_confirm_tooltip')"
        placement="top"
      >
        <InfoCircleIcon class="-ml-1 w-4 pb-0.5 text-gray-500" />
      </UITooltip>
    </div>
  </div>

  <DependentSettings
    v-if="state.showDependentSettings"
    :show-modal="state.showDependentSettings"
    :title="t('calendar_advanced.forms_payment.change_form_order')"
    :desc="
      t('calendar_advanced.forms_payment.change_form_order_desc', {
        contactLabel: contactLabelSingularLower,
      })
    "
    @cancel-click="cancelDependentSettings"
    @confirm-click="confirmDependentSettings"
  />

  <AddGuestModal
    v-model:show="showModal"
    v-model:chargeGuestsEnabled="
      AppState.editCalendarValidationData.enableChargeGuests
    "
    @onConfirm="handleGuestModalConfirm"
  />
</template>